import '../../styles/restScreens.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import ActionPillMenu from '../ui/ActionPillMenu';
import { formatPhonesInText } from '../../lib/phone';
import {
    buildCrmActivityTimelineEntries,
    filterCrmActivityTimelineEntries,
} from '../../lib/crmActivityTimeline';

const COMMUNICATION_CHANNELS = ['sms', 'mms', 'whatsapp', 'email'];
const FILTERS = [
    ['all', 'All'],
    ['communication', 'Communications'],
    ['note', 'Notes'],
    ['call', 'Calls'],
    ['meeting', 'Meetings'],
    ['email', 'Email'],
    ['text', 'Text'],
    ['stage_change', 'Stage changes'],
];

const ACTIVITY_LABELS = {
    call: 'Call',
    email: 'Email',
    text: 'Text',
    meeting: 'Meeting',
    note: 'Note',
    stage_change: 'Stage change',
};

function formatDate(value) {
    if (!value) return 'Date unavailable';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Date unavailable' : date.toLocaleString();
}

function recordTypeLabel(recordType) {
    return recordType === 'opportunity'
        ? 'Opportunity'
        : recordType === 'contact'
            ? 'Contact'
            : 'Account';
}

function contactAddressValues(contact, key) {
    const values = Array.isArray(contact?.[key]) ? contact[key] : [];
    return [...new Set(values.map((entry) => {
        if (typeof entry === 'string') return entry.trim();
        if (key !== 'phones' || !entry || typeof entry !== 'object') return '';
        return String(entry.number || entry.phone || entry.value || entry.phone_number || '').trim();
    }).filter(Boolean))];
}

function CrmActivityTimeline({ account, contact, communicationsOnly = false }) {
    const [activities, setActivities] = useState([]);
    const [communications, setCommunications] = useState([]);
    const [staffNames, setStaffNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState(communicationsOnly ? 'communication' : 'all');
    const [activityType, setActivityType] = useState('note');
    const [targetRecordType, setTargetRecordType] = useState(contact?.id ? 'contact' : 'account');
    const [body, setBody] = useState('');

    useEffect(() => {
        if (communicationsOnly) setFilter('communication');
    }, [communicationsOnly]);

    useEffect(() => {
        if (!contact?.id) setTargetRecordType('account');
    }, [contact?.id]);

    const loadTimeline = useCallback(async () => {
        if (!account?.id || !account?.org_id) {
            setError('Timeline unavailable: the client is missing its organization scope.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const opportunityQueries = [
                supabaseClient
                    .from('opportunities')
                    .select('id')
                    .eq('account_id', account.id),
            ];
            if (contact?.id) {
                opportunityQueries.push(
                    supabaseClient
                        .from('opportunities')
                        .select('id')
                        .eq('contact_id', contact.id)
                );
            }
            const [accountOpportunities, contactOpportunities] = await Promise.all(opportunityQueries);
            if (accountOpportunities.error) throw accountOpportunities.error;
            if (contactOpportunities?.error) throw contactOpportunities.error;

            const recordIds = [...new Set([
                account.id,
                contact?.id,
                ...(accountOpportunities.data || []).map((row) => row.id),
                ...(contactOpportunities?.data || []).map((row) => row.id),
            ].filter(Boolean))];

            const communicationQueries = [
                supabaseClient
                    .from('intake_sessions')
                    .select('id, channel, email_subject, last_inbound_at, last_outbound_at, updated_at, created_at')
                    .eq('org_id', account.org_id)
                    .eq('account_id', account.id)
                    .in('channel', COMMUNICATION_CHANNELS)
                    .order('updated_at', { ascending: false }),
                ...contactAddressValues(contact, 'phones').map((phone) => supabaseClient
                    .from('intake_sessions')
                    .select('id, channel, email_subject, last_inbound_at, last_outbound_at, updated_at, created_at')
                    .eq('org_id', account.org_id)
                    .eq('contact_phone', phone)
                    .in('channel', COMMUNICATION_CHANNELS)
                    .order('updated_at', { ascending: false })),
                ...contactAddressValues(contact, 'emails').map((email) => supabaseClient
                    .from('intake_sessions')
                    .select('id, channel, email_subject, last_inbound_at, last_outbound_at, updated_at, created_at')
                    .eq('org_id', account.org_id)
                    .eq('contact_email', email)
                    .in('channel', COMMUNICATION_CHANNELS)
                    .order('updated_at', { ascending: false })),
            ];
            const [activityResult, ...communicationResults] = await Promise.all([
                supabaseClient
                    .from('crm_activities')
                    .select('id, record_type, record_id, activity_type, body, occurred_at, created_by, created_at')
                    .eq('org_id', account.org_id)
                    .in('record_id', recordIds)
                    .order('occurred_at', { ascending: false }),
                ...communicationQueries,
            ]);
            if (activityResult.error) throw activityResult.error;
            const communicationError = communicationResults.find((result) => result.error)?.error;
            if (communicationError) throw communicationError;
            const loadedCommunications = [...new Map(
                communicationResults.flatMap((result) => result.data || []).map((thread) => [thread.id, thread])
            ).values()];

            const loadedActivities = activityResult.data || [];
            const authorIds = [...new Set(loadedActivities.map((item) => item.created_by).filter(Boolean))];
            let names = {};
            if (authorIds.length) {
                const { data: profiles, error: profileError } = await supabaseClient
                    .from('user_profiles')
                    .select('id, full_name')
                    .in('id', authorIds);
                if (profileError) throw profileError;
                names = Object.fromEntries((profiles || []).map((profile) => [profile.id, profile.full_name]));
            }
            setActivities(loadedActivities);
            setCommunications(loadedCommunications);
            setStaffNames(names);
        } catch (loadError) {
            setError(loadError.message || 'Could not load the CRM timeline.');
        } finally {
            setLoading(false);
        }
    }, [account?.id, account?.org_id, contact]);

    useEffect(() => {
        loadTimeline();
    }, [loadTimeline]);

    const entries = useMemo(() => buildCrmActivityTimelineEntries({
        activities,
        communications,
        staffNames,
        communicationAuthor: contact?.name || account?.account_name || 'Communications',
    }), [activities, communications, staffNames, contact?.name, account?.account_name]);
    const visibleEntries = useMemo(
        () => filterCrmActivityTimelineEntries(entries, filter),
        [entries, filter]
    );

    const chooseActivity = useCallback((type) => {
        setActivityType(type);
        setFilter(type);
    }, []);

    const activityMenuOptions = useMemo(() => [
        {
            key: 'note',
            label: 'Add note',
            onSelect: () => chooseActivity('note'),
        },
        {
            key: 'interaction',
            label: 'Log interaction',
            placement: 'right',
            options: ['call', 'meeting', 'email', 'text'].map((type) => ({
                key: type,
                label: `Log ${type}`,
                onSelect: () => chooseActivity(type),
            })),
        },
    ], [chooseActivity]);

    const saveActivity = async (event) => {
        event.preventDefault();
        const trimmedBody = body.trim();
        if (!trimmedBody || saving) return;
        const targetId = targetRecordType === 'contact' ? contact?.id : account?.id;
        if (!targetId) {
            setError('Choose a client record before adding an activity.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const { error: insertError } = await supabaseClient
                .from('crm_activities')
                .insert({
                    org_id: account.org_id,
                    entity_id: account.entity_id,
                    record_type: targetRecordType,
                    record_id: targetId,
                    activity_type: activityType,
                    body: trimmedBody,
                    occurred_at: new Date().toISOString(),
                });
            if (insertError) throw insertError;
            setBody('');
            await loadTimeline();
        } catch (saveError) {
            setError(saveError.message || 'Could not save this activity.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <section data-m6-layout="clients" data-m6-component="CrmActivityTimeline" className="space-y-4" aria-label="CRM activity timeline">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-[var(--ink)]">
                        {communicationsOnly ? 'Client communications' : 'Activity timeline'}
                    </h3>
                    <p className="text-xs text-[var(--ink-muted)]">
                        {communicationsOnly
                            ? 'Linked threads open in Communications and remain read-only here.'
                            : 'Newest activity first. Saved entries cannot be edited or deleted.'}
                    </p>
                </div>
                {!communicationsOnly && (
                    <ActionPillMenu
                        menuLabel="Add CRM activity"
                        triggerLabel="Add activity"
                        triggerTitle="Add a note or log an interaction"
                        triggerClassName="ia-hover-pulse rounded-full border border-[var(--surface-border)] bg-transparent px-3 py-2 text-sm font-medium text-[var(--ink)]"
                        options={activityMenuOptions}
                    >
                        Add activity
                    </ActionPillMenu>
                )}
            </div>

            {!communicationsOnly && (
                <form onSubmit={saveActivity} className="space-y-3 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-3">
                    <div className="flex flex-wrap items-end gap-3">
                        <label className="min-w-36 space-y-1 text-xs text-[var(--ink-muted)]">
                            <span>Entry type</span>
                            <select
                                aria-label="Activity type"
                                value={activityType}
                                onChange={(event) => setActivityType(event.target.value)}
                                className="block w-full rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)]"
                            >
                                {['note', 'call', 'meeting', 'email', 'text'].map((type) => (
                                    <option key={type} value={type}>{ACTIVITY_LABELS[type]}</option>
                                ))}
                            </select>
                        </label>
                        {contact?.id && (
                            <label className="min-w-36 space-y-1 text-xs text-[var(--ink-muted)]">
                                <span>Attach to</span>
                                <select
                                    aria-label="Activity record"
                                    value={targetRecordType}
                                    onChange={(event) => setTargetRecordType(event.target.value)}
                                    className="block w-full rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)]"
                                >
                                    <option value="contact">Primary contact</option>
                                    <option value="account">Account</option>
                                </select>
                            </label>
                        )}
                        <button
                            type="submit"
                            disabled={saving || !body.trim()}
                            className="ia-hover-pulse rounded-full border border-[var(--surface-border)] bg-transparent px-3 py-1.5 text-sm font-medium text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : 'Save activity'}
                        </button>
                    </div>
                    <textarea
                        aria-label="Activity details"
                        value={body}
                        onChange={(event) => setBody(event.target.value)}
                        rows={3}
                        placeholder={`Describe the ${ACTIVITY_LABELS[activityType].toLowerCase()}…`}
                        className="w-full rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-3 py-2 text-sm text-[var(--ink)] placeholder:text-[var(--ink-muted)]"
                    />
                </form>
            )}

            <div className="flex flex-wrap gap-2" aria-label="Filter timeline by type">
                {FILTERS.filter(([value]) => !communicationsOnly || value === 'communication').map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        aria-pressed={filter === value}
                        onClick={() => setFilter(value)}
                        className={`ia-hover-pulse rounded-full border bg-transparent px-3 py-1 text-xs font-medium transition ${
                            filter === value
                                ? 'ia-selected-pill'
                                : 'border-[var(--surface-border)] text-[var(--ink-muted)]'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {error && <p role="alert" className="text-sm text-[var(--ui-alert)]">{error}</p>}
            {loading ? (
                <p className="text-sm text-[var(--ink-muted)]">Loading timeline…</p>
            ) : visibleEntries.length ? (
                <ol className="divide-y divide-[var(--surface-border)] rounded-lg border border-[var(--surface-border)]">
                    {visibleEntries.map((entry) => (
                        <li key={entry.id} className="space-y-1 p-3">
                            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                                <span className="text-xs font-semibold text-[var(--ink)]">
                                    {entry.kind === 'communication'
                                        ? 'Communication thread'
                                        : `${ACTIVITY_LABELS[entry.activityType] || entry.activityType} · ${recordTypeLabel(entry.recordType)}`}
                                </span>
                                <time className="text-xs text-[var(--ink-muted)]" dateTime={entry.occurredAt || undefined}>
                                    {formatDate(entry.occurredAt)}
                                </time>
                            </div>
                            {entry.kind === 'communication' ? (
                                <a
                                    href={entry.href}
                                    className="text-sm text-[var(--ink)] underline decoration-[var(--surface-border)] underline-offset-2"
                                >
                                    {entry.body} ↗
                                </a>
                            ) : (
                                <p className="whitespace-pre-wrap text-sm text-[var(--ink)]">{formatPhonesInText(entry.body)}</p>
                            )}
                            <p className="text-xs text-[var(--ink-muted)]">
                                {entry.kind === 'communication' ? 'Read-only · ' : ''}{entry.author}
                            </p>
                        </li>
                    ))}
                </ol>
            ) : (
                <p className="rounded-lg border border-[var(--surface-border)] p-4 text-sm text-[var(--ink-muted)]">
                    {communicationsOnly ? 'No linked SMS, MMS, WhatsApp, or email threads.' : 'No timeline entries yet.'}
                </p>
            )}
        </section>
    );
}

export default CrmActivityTimeline;
